import { unstable_batchedUpdates } from 'react-dom';
import { notifyManager, QueryClient } from '@tanstack/react-query';

import { isCreateRootEnabled } from 'utils/reactRoot';

const runUnbatched = <T>(callback: () => T): T => callback();

const batchForLegacyRoots = (): (<T>(callback: () => T) => T) =>
  isCreateRootEnabled() ? runUnbatched : unstable_batchedUpdates;

const CACHE_WRITES = [
  'cancelQueries',
  'removeQueries',
  'setQueriesData',
  'setQueryData',
  'invalidateQueries',
  'refetchQueries',
  'resetQueries',
] as const;

type Method = (...args: unknown[]) => unknown;

/**
 * @deprecated A bridge from react-query v3 behaviour to TanStack Query v5. Don't use it for new clients, and delete
 * each part once its condition is met; the whole helper goes when the last part does. It only applies to clients
 * passed to this function.
 *
 * - Batching: both apps mount legacy ReactDOM.render roots unless `feature_flag_react_18_create_root` is on. There,
 *   React renders every setState outside a React event synchronously; v3 batched its notifications itself and v5
 *   leaves it to createRoot, so without this a broad invalidation re-renders once per query and froze TQ. With the
 *   flag on, React batches and this part is a no-op. Remove it with the legacy path of mountReactRoot.
 */
export function deprecated_withV3Semantics<TClient extends QueryClient>(client: TClient): TClient {
  const batch = batchForLegacyRoots();
  notifyManager.setBatchNotifyFunction(batch);
  for (const cache of [client.getQueryCache(), client.getMutationCache()]) {
    const notify = cache.notify.bind(cache) as Method;
    Object.assign(cache, { notify: (event: unknown) => batch(() => notify(event)) });
  }
  const methods = client as unknown as Record<(typeof CACHE_WRITES)[number], Method>;
  for (const name of CACHE_WRITES) {
    const original = methods[name].bind(client);
    methods[name] = (...args: unknown[]) => batch(() => original(...args));
  }
  return client;
}
